import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import BackgroundImage from 'gatsby-background-image';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { convertToBgImage } from 'gbimage-bridge';
import { rem } from 'polished';
import React from 'react';
import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';

import StoreButtons from '~/components/StoreButtons';
import { MOBILE_SCREEN_WIDTH, PC_SCREEN_WIDTH } from '~/constants/screen';
import { css, scaledCss } from '~/css';
import type { KeyVisualQuery } from '~/generated/types/gatsby';
import { useFrontmatter } from '~/shared/contexts/frontmatter';
import { mq } from '~/shared/utils/mq';

interface BackgroundImageProps {
  minHeight: number;
}

const PcBackgroundImage = styled(BackgroundImage)((props: BackgroundImageProps) => {
  const size = props.minHeight;
  return css({
    display: ['none', 'block'],
    minHeight: `${size}px`,
    height: `${(size / PC_SCREEN_WIDTH) * 100}vw`,
  })(props);
});

const MobileBackgroundImage = styled(BackgroundImage)((props: BackgroundImageProps) => {
  const size = props.minHeight;
  return css({
    display: ['block', 'none'],
    minHeight: `${size}px`,
    height: `${(size / MOBILE_SCREEN_WIDTH) * 100}vw`,
  })(props);
});

const Image = styled(
  GatsbyImage,
  {},
)(
  css({
    width: '100%',
    height: '100%',
  }),
);

const ContentLayout = styled.div(
  mq({
    position: 'absolute',
    top: ['50%', 'unset'],
    bottom: ['unset', 0],

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: rem(8),

    paddingBottom: rem(45),
    width: '100%',
  }),
);

const GameKeyVisual: React.FC = () => {
  const frontmatter = useFrontmatter();
  const locale = useLocale();

  const data = useStaticQuery<KeyVisualQuery>(graphql`
    query KeyVisualLayout {
      allStrapiGamesiteMain {
        nodes {
          locale
          environment
          game
          logo {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 500, breakpoints: [360, 640, 1200], formats: [AUTO, WEBP])
              }
            }
          }
          mainImageMobile {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 360, breakpoints: [360, 640], formats: [AUTO, WEBP])
              }
            }
          }
          mainImagePc {
            localFile {
              childImageSharp {
                gatsbyImageData(width: 1920, breakpoints: [640, 1200], formats: [AUTO, WEBP])
              }
            }
          }
        }
      }
    }
  `);

  const { logo } = frontmatter.top;
  const gameNodes = data.allStrapiGamesiteMain.nodes?.filter(
    (node) => node.environment === (process.env.GATSBY_ENVIRONMENT ?? 'preview') && node.game === frontmatter.gameId,
  );
  const gameNode = gameNodes?.find((node) => node.locale.toLowerCase() === locale.toLowerCase());
  const logoNode = gameNode?.logo.localFile;
  const mainImagePc = gameNode?.mainImagePc;
  const mainImageMobile = gameNode?.mainImageMobile;

  return (
    <>
      {mainImagePc && logoNode && (
        <PcBackgroundImage
          Tag='section'
          {...convertToBgImage(getImage(mainImagePc.localFile))}
          minHeight={mainImagePc.localFile.childImageSharp.gatsbyImageData.height}
        >
          <ContentLayout>
            <div
              css={css({
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: [logo.pc.h * 0.8, logo.pc.h * 0.8, logo.pc.h * 0.8, logo.pc.h],
                ...(logo.pc.margin && { marginBottom: logo.pc.margin }),
              })}
            >
              <Image alt='logo' loading='eager' objectFit='contain' image={logoNode.childImageSharp.gatsbyImageData} />
            </div>
            <StoreButtons />
          </ContentLayout>
        </PcBackgroundImage>
      )}
      {mainImageMobile && logoNode && (
        <MobileBackgroundImage
          Tag='section'
          {...convertToBgImage(getImage(mainImageMobile.localFile))}
          minHeight={mainImageMobile.localFile.childImageSharp.gatsbyImageData.height}
        >
          <ContentLayout>
            <div
              css={scaledCss({
                width: logo.mobile.w,
                height: logo.mobile.h,
                ...(logo.mobile.margin && { marginBottom: logo.mobile.margin }),
              })}
            >
              <Image alt='logo' loading='eager' objectFit='contain' image={logoNode.childImageSharp.gatsbyImageData} />
            </div>
            <StoreButtons />
          </ContentLayout>
        </MobileBackgroundImage>
      )}
    </>
  );
};

export default GameKeyVisual;
